import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AcademyRichText } from '@/pages/learn/academy/AcademyRichText';
import { StudySelfAssessmentAnswers } from '@/pages/learn/study/StudySelfAssessmentAnswers';
import {
  readSelfAssessment,
  selfAssessmentKey,
  selfAssessmentPath,
} from '@/pages/learn/study/studySelfAssessmentApi';
import { getSavedWorking, WORKING_LIBRARY_QUERY_KEY } from '@/pages/learn/study/workingCanvasApi';
import { DEMO_ANALYSIS_ID } from './demoLearningOutcomes';
import { DEMO_ASSESSMENT_ID } from './demoAssessments';
import { STUDY_DEMO_CASES } from './exampleData';

/** Complete results use the same saved data, answer renderer and editable detail routes. */
export function StudyDemoAssessmentResult({ subjectKey }: { subjectKey: string }) {
  const paper = useQuery({
    queryKey: selfAssessmentKey(DEMO_ANALYSIS_ID, DEMO_ASSESSMENT_ID),
    queryFn: () => readSelfAssessment(DEMO_ANALYSIS_ID, DEMO_ASSESSMENT_ID),
    retry: false,
  });
  if (paper.isPending) return <p role="status">Opening completed quiz…</p>;
  if (!paper.data)
    return (
      <p role="alert">
        The completed example could not open.{' '}
        <button className="underline" onClick={() => void paper.refetch()}>
          Retry example
        </button>
      </p>
    );
  return (
    <section aria-label="Completed assessment example">
      <h3 className="text-xl font-extrabold">Complete example: {paper.data.assessment.title}</h3>
      <p className="mt-2 text-sm">
        Five prepared questions, submitted answers and full explanations. Fictional Demo · no live
        AI or Stars.
      </p>
      <Link
        className="mt-2 inline-block min-h-11 font-bold underline"
        to={selfAssessmentPath(DEMO_ANALYSIS_ID, DEMO_ASSESSMENT_ID, subjectKey)}
      >
        Open this quiz on its own page
      </Link>
      <StudySelfAssessmentAnswers paper={paper.data} subjectKey={subjectKey} />
    </section>
  );
}
export function StudyDemoWorkingResult() {
  const example = STUDY_DEMO_CASES.find((row) => row.key === 'linear')!;
  const page = useQuery({
    queryKey: [...WORKING_LIBRARY_QUERY_KEY, 'demo-complete', example.workingId],
    queryFn: () => getSavedWorking(example.workingId),
    retry: false,
    refetchOnMount: 'always',
  });
  if (page.isPending) return <p role="status">Opening completed working…</p>;
  if (!page.data)
    return (
      <p role="alert">
        The completed working could not open.{' '}
        <button className="underline" onClick={() => void page.refetch()}>
          Retry example
        </button>
      </p>
    );
  return (
    <section
      aria-label="Completed working example"
      className="mt-5 rounded-3xl border border-hairline bg-canvas-pure p-5"
    >
      <h2 className="text-xl font-extrabold">Complete working: solve, explain and check</h2>
      <p className="mt-2 text-sm text-slate2">
        Fictional pre-generated example. This is a saved reasoning page, not a marked quiz.
      </p>
      <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed">
        <AcademyRichText text={page.data.content.text} />
      </div>
      <Link
        className="btn-pill-secondary mt-4 inline-flex"
        to={`/learn/study/working/${page.data.id}`}
      >
        Continue editing this working
      </Link>
    </section>
  );
}
